import React, { useCallback, useMemo, useState } from 'react';

import Window from '../../../components/Window';
import { PatientActivationIdentity } from '../../../services/patient/PatientActivation';
import Identification from './Identification';
import ConsentAndPassword from './ConsentAndPassword';
import WhatsappLink from './WhatsappLink';
import FinishActivation from './FinishActivation';

import { Container } from './styles';

/**
 * Ativação da conta do paciente pelo link do e-mail de boas-vindas (tarefa 0.33).
 *
 * ⚠️ **Não é a `/welcome`**, que é do usuário de equipe. Aquela pede telefone (paciente pode
 * não ter), mostra o login como `username` (paciente não tem `username` — o login dele é o
 * e-mail) e termina mandando entrar no painel web, que paciente não usa. Esta termina no
 * aplicativo, e é aqui que o **termo de consentimento** é lido e aceito pelo titular.
 *
 * ⚠️ **Rota pública, e o `routes/Route.tsx` manda visitante logado para `/dashboard/profile`.**
 * Quem cadastrou o paciente e clicar no link dele no mesmo navegador não vê esta tela — é o
 * mesmo comportamento que a `/welcome` já tem.
 *
 * **Identidade antes do termo, de propósito:** o texto do termo traz nome e CPF do paciente,
 * então ele só chega à tela depois de a identidade estar provada. Ver o comentário do
 * `IdentifyPatientActivationService`, no back.
 *
 * ⓘ **O terceiro passo (0.34) é o vínculo de WhatsApp, e é opcional.** Ele vem **depois** do
 * termo por dois motivos que apontam para o mesmo lado: o consentimento precisa estar
 * assinado antes de a pessoa ser registrada no `cinco_chat`, e é a ativação que emite a
 * sessão que aquele passo usa — o token do convite morre ali. Sem sessão emitida, a tela vai
 * direto para o fim.
 */
const PatientWelcome: React.FC = () => {
  const [stepKey, setStepKey] = useState('Identification');
  const [identity, setIdentity] = useState<PatientActivationIdentity>();
  const [cpf, setCpf] = useState('');
  const [birthday, setBirthday] = useState('');
  /**
   * ⚠️ **Em estado, nunca no `localStorage`.** É sessão de paciente num navegador que pode
   * ter a sessão da equipe guardada (`@Cinco:token`); gravá-la lá trocaria uma pela outra.
   */
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [whatsappLinked, setWhatsappLinked] = useState(false);

  /**
   * CPF e nascimento ficam guardados porque o **passo 2 os manda outra vez**: o back
   * reconfere. Não é redundância — a `/welcome` do usuário de equipe confirma a identidade
   * no passo 1 e o passo 2 aceita só o token, o que torna aquela confirmação decorativa.
   */
  const handleIdentified = useCallback(
    (dados: {
      identity: PatientActivationIdentity;
      cpf: string;
      birthday: string;
    }) => {
      setIdentity(dados.identity);
      setCpf(dados.cpf);
      setBirthday(dados.birthday);
      setStepKey('ConsentAndPassword');
    },
    [],
  );

  /**
   * A conta já está criada e o termo assinado quando isto roda. Daí em diante nada pode
   * impedir a conclusão: sem sessão emitida, o passo do WhatsApp não tem como acontecer e a
   * tela conclui direto.
   */
  const handleActivated = useCallback(
    (dados: { sessionToken: string | null }) => {
      setSessionToken(dados.sessionToken);
      setStepKey(dados.sessionToken ? 'WhatsappLink' : 'FinishActivation');
    },
    [],
  );

  const handleWhatsappFinish = useCallback(
    (dados: { whatsappLinked: boolean }) => {
      setWhatsappLinked(dados.whatsappLinked);
      setStepKey('FinishActivation');
    },
    [],
  );

  const steps = useMemo(
    () => [
      {
        key: 'Identification',
        component: <Identification onIdentified={handleIdentified} />,
      },
      {
        key: 'ConsentAndPassword',
        component: identity ? (
          <ConsentAndPassword
            identity={identity}
            cpf={cpf}
            birthday={birthday}
            onActivated={handleActivated}
          />
        ) : null,
      },
      {
        key: 'WhatsappLink',
        component: sessionToken ? (
          <WhatsappLink token={sessionToken} onFinish={handleWhatsappFinish} />
        ) : null,
      },
      {
        key: 'FinishActivation',
        component: <FinishActivation whatsappLinked={whatsappLinked} />,
      },
    ],
    [
      identity,
      cpf,
      birthday,
      sessionToken,
      whatsappLinked,
      handleIdentified,
      handleActivated,
      handleWhatsappFinish,
    ],
  );

  return (
    <Window>
      <Container>
        {steps.find((step) => step.key === stepKey)?.component}
      </Container>
    </Window>
  );
};

export default PatientWelcome;
